/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";

import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";

import { GridCellEditStopReasons, GridPreProcessEditCellProps, GridRenderCellParams } from "@mui/x-data-grid";
import { useWindowDimensions } from "../../utils/Hooks";
import ConfirmDeleteModal from "../../components/confirmation/ConfirmDeleteModal";
import CreateMaterialModal from "./CreateMaterialModal";
import DataTable from "../../components/table/DataTable";
import { Effect, Material, Nullable } from "../../utils/Interfaces";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import EditMaterialModal from "./EditMaterialModal";
import EditIcon from "@mui/icons-material/Edit";
import { useDeleteMaterialDatabaseEntry, useEditMaterialDatabaseEntryByKey, useMaterialsDatabase } from "./materialsHooks";
import { delayedAction, isNumeric } from "../../utils/Functions";

function MaterialsDatabase() {
    const { height } = useWindowDimensions();

    const { data, status: loadStatus } = useMaterialsDatabase();

    const [addMaterialOpen, setAddMaterialOpen] = useState<boolean>(false);
    const [selectedMaterial, setSelectedMaterial] = useState<Nullable<Material>>(null);

    const [editMaterialModalOpen, setEditMaterialModalOpen] = useState<boolean>(false);
    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState<boolean>(false);

    const { mutate: editMaterialByKey } = useEditMaterialDatabaseEntryByKey(selectedMaterial?.id ?? 0, () => {});
    const { mutate: deleteMaterial } = useDeleteMaterialDatabaseEntry(selectedMaterial?.id ?? 0, () => {});

    function cellEditStop(params: any, event: any) {
        if (params.reason === GridCellEditStopReasons.cellFocusOut) {
            event.defaultMuiPrevented = true;
        }

        if (params.reason === GridCellEditStopReasons.escapeKeyDown) {
            return;
        }

        if (!event.target) {
            return;
        }

        // If no raw changes are detected, do not initiate a web request.
        if (event.target.value === undefined || event.target.value === params.value) {
            return;
        }

        let value = event.target.value?.replaceAll("$", "") ?? "";
        if (params.field === "price") {
            if (!value.includes(".")) {
                value += ".00";
            }
            value = parseFloat(value);
        }

        if (params.field === "sales_tax") {
            if (!value.includes(".")) {
                value += ".00";
            }
            value = parseFloat(value);
        }

        if (params.field === "shipping_price") {
            if (!value.includes(".")) {
                value += ".00";
            }
            value = parseFloat(value);
        }

        if (params.field === "markup") {
            // Divide markup by 100 to convert back from percent to float.
            value = parseFloat(value) / 100;
        }

        if (params.field === "amount") {
            value = parseInt(value);
        }

        editMaterialByKey([params.field, value, data?.results ?? []]);
    }

    const columns = [
        {
            field: "edit",
            width: 60,
            headerName: "Edit",
            sortable: false,
            filterable: false,
            editable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title="Edit Part/Material">
                        <IconButton
                            onClick={() => {
                                setSelectedMaterial(params.row);
                                setEditMaterialModalOpen(true);
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "delete",
            width: 60,
            headerName: "Delete",
            sortable: false,
            filterable: false,
            editable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title="Delete Part/Material">
                        <IconButton
                            onClick={() => {
                                setSelectedMaterial(params.row);
                                setConfirmDeleteModalOpen(true);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "name",
            width: 200,
            headerName: "Part Name",
            sortable: true,
            filterable: true,
            editable: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={params.row.name}>
                        <Typography variant="subtitle2" sx={{ textOverflow: "ellipsis", width: 195 }} noWrap>
                            {params.row.name}
                        </Typography>
                    </Tooltip>
                );
            },
        },
        {
            field: "manufacturer",
            width: 120,
            headerName: "Manufacturer",
            sortable: true,
            filterable: true,
            editable: true,
        },
        {
            field: "part_number",
            width: 180,
            headerName: "P/N",
            sortable: true,
            filterable: true,
            editable: true,
        },
        {
            field: "price",
            width: 120,
            headerName: "Unit Price",
            sortable: true,
            filterable: true,
            editable: true,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = !isNumeric(params.props.value);
                return { ...params.props, error: hasError };
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography
                        sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}
                    >{`$${parseFloat(params.value).toFixed(2)}`}</Typography>
                );
            },
        },
        {
            field: "shipping_price",
            width: 120,
            headerName: "Shipping Price",
            sortable: true,
            filterable: true,
            editable: true,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = !isNumeric(params.props.value);
                return { ...params.props, error: hasError };
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography
                        sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}
                    >{`$${parseFloat(params.value).toFixed(2)}`}</Typography>
                );
            },
        },
        {
            field: "sales_tax",
            width: 120,
            headerName: "Sales Tax",
            sortable: true,
            filterable: true,
            editable: true,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = !isNumeric(params.props.value);
                return { ...params.props, error: hasError };
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography
                        sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}
                    >{`$${parseFloat(params.value).toFixed(2)}`}</Typography>
                );
            },
        },
        {
            field: "acquisition_price",
            width: 120,
            headerName: "Acquisition Price",
            sortable: true,
            filterable: true,
            editable: false,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = !isNumeric(params.props.value);
                return { ...params.props, error: hasError };
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography
                        color="info.main"
                        sx={{ display: "flex", flexDirection: "row", justifyContent: "center", cursor: "not-allowed" }}
                    >
                        {`$${parseFloat(params.value).toFixed(2)}`}
                    </Typography>
                );
            },
        },
        {
            field: "markup",
            width: 120,
            headerName: "Markup %",
            sortable: true,
            filterable: true,
            editable: true,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = !isNumeric(params.props.value);
                return { ...params.props, error: hasError };
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography
                        sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}
                    >{`${parseFloat(params.value).toFixed(2)}%`}</Typography>
                );
            },
        },
        {
            field: "retail_price",
            width: 120,
            headerName: "Retail Price",
            sortable: true,
            filterable: true,
            editable: false,
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                const hasError = !isNumeric(params.props.value);
                return { ...params.props, error: hasError };
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography
                        color="info.main"
                        sx={{ display: "flex", flexDirection: "row", justifyContent: "end", cursor: "not-allowed" }}
                    >
                        {`$${parseFloat(params.value).toFixed(2)}`}
                    </Typography>
                );
            },
        },
        {
            field: "notes",
            width: 200,
            headerName: "Comments/Notes",
            sortable: true,
            filterable: true,
            editable: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={params.row.notes}>
                        <Typography variant="subtitle2" sx={{ textOverflow: "ellipsis", width: 195 }} noWrap>
                            {params.row.notes}
                        </Typography>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Box>
            <CreateMaterialModal
                allMaterials={data?.results ?? []}
                open={addMaterialOpen}
                handleClose={() => setAddMaterialOpen(false)}
            />

            <EditMaterialModal
                material={selectedMaterial}
                allMaterials={data?.results ?? []}
                open={editMaterialModalOpen}
                handleClose={() => setEditMaterialModalOpen(false)}
            />

            <ConfirmDeleteModal
                confirmText={`delete ${selectedMaterial?.part_number}`}
                description="You are about to delete the following part from the database. Any occurrences connected to a Trip will also be deleted."
                handleClose={() => setConfirmDeleteModalOpen(false)}
                open={confirmDeleteModalOpen}
                onDelete={(modalCallback: Effect) => {
                    deleteMaterial([data?.results ?? []]);

                    // FIXME This is a hack because the ConfirmDelete callback cannot easily be integrated with the workflow of React Query.
                    void delayedAction(300, () => {
                        modalCallback();
                    });
                }}
            />

            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Parts/Materials", route: "/?section=materials" },
                    { text: "Database", route: "/materials/database" },
                ]}
            />

            {loadStatus === "pending" && (
                <Stack direction="row" justifyContent="center" width="100%" height={height / 2 + 100}>
                    <Stack direction="column" justifyContent="center">
                        <CircularProgress size={100} />
                    </Stack>
                </Stack>
            )}

            {loadStatus === "success" && (
                <DataTable
                    tableName="MATERIALS_DATABASE"
                    cellEditStop={cellEditStop}
                    cellEditStart={(params, event) => setSelectedMaterial(params)}
                    height={height - 180}
                    allowRowSelected={false}
                    columns={columns}
                    data={data.results ?? []}
                >
                    <Button
                        onClick={() => {
                            setAddMaterialOpen(true);
                        }}
                    >
                        <UploadFileIcon fontSize="small" />
                        &nbsp; Add New Part/Material
                    </Button>
                </DataTable>
            )}
        </Box>
    );
}

export default MaterialsDatabase;
